import { Box, Button, IconButton, Tab, Tabs, Typography } from "@mui/material"
import { useState } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { setPerms } from "../../../redux/slices/rolesAndPermsSlice"
import { setLoading } from "../../../redux/slices/appSlice"
import NewRoleModal from "../../Modal/NewRoleModal"
import { colors } from "../../../../styles/commonStyles"
import { getTokenLocal } from "../../../../helper/utils"
import UsersAPI from "../../../../api/users"
import { useResponseHandle } from "../../../hooks"
import { useNavigate } from "react-router-dom"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import RoleEditMenu from "../../Menu/RoleEditMenu"
import { SimpleConfirmation } from "../../Modal/Reusable"
import PermsWrapper from "../../Auth/PermsWrapper"
import { permissions } from "../../../../config/constants"

type Props = {
  fetchAllRolesAndPerms: any
}

type RoleType = {
  role_id: any
  role_display_name: string
  count: number
  name: string
}

const usersAPI = new UsersAPI()

const RolesTable = (props: Props) => {
  const { fetchAllRolesAndPerms } = props
  const token = getTokenLocal()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const allPerms = useSelector((state: any) => {
    return state.rolesAndPerms.userPerms
  })

  const state = useSelector((state: any) => {
    return state.rolesAndPerms
  })

  const roles = state.roles

  const [selectedRoleType, setSelectedRoleType] = useState(0)
  const [deleteRoleId, setDeleteRoleId] = useState("")
  const [open, setOpen] = useState(false)
  const [roleDeleteConfirmationOpen, setRoleDeleteConfirmationOpen] =
    useState(false)
  const [loader, setLoader] = useState(false)
  const [editState, setEditState] = useState({})
  const [isDisabled, setIsDisabled] = useState(false)

  const handleCreateNewRole = async (
    id: any,
    title: any,
    description: any,
    roleCategory: any,
    resetAndCloseHandler: any,
  ) => {
    if (token) {
      setLoader(true)
      const response = await usersAPI.createNewRole(
        token,
        id,
        title,
        description,
        roleCategory,
      )
      const result = useResponseHandle({
        apiResponse: response,
        successMessage: true,
      })
      resetAndCloseHandler()
      if (result?.isAuthorized) {
        setIsDisabled(false)
        if (result?.statusCode == 200) {
          await fetchAllRolesAndPerms()
        }
      } else {
        navigate("/auth")
      }
      setIsDisabled(false)
      setLoader(false)
      return result
    }
  }

  const handleEditRole = async (
    id: any,
    title: any,
    description: any,
    resetAndCloseHandler: any,
  ) => {
    if (token) {
      setLoader(true)
      const response = await usersAPI.updateRole(token, id, title, description)
      const result = useResponseHandle({
        apiResponse: response,
        successMessage: true,
      })
      resetAndCloseHandler()
      if (result?.isAuthorized) {
        if (result?.statusCode == 200) {
          await fetchAllRolesAndPerms()
        }
      } else {
        navigate("/auth")
      }
      setIsDisabled(false)

      setLoader(false)
      return result
    }
  }

  const handleDeleteRole = async () => {
    if (token) {
      setLoader(true)
      const response = await usersAPI.deleteRole(token, deleteRoleId)
      const result = useResponseHandle({
        apiResponse: response,
        successMessage: true,
      })

      if (result?.isAuthorized) {
        if (result?.statusCode == 200) {
          await fetchAllRolesAndPerms()
        }
      } else {
        navigate("/auth")
      }
      setLoader(false)
      return result
    }
  }

  const handleRoleTypeSelection = async (selectedIndex: number) => {
    dispatch(setLoading(true))

    await fetchAllRolesAndPerms()

    setSelectedRoleType(selectedIndex)

    const currentModules: any = []

    const modules = roles[selectedIndex || 0].modules
    const role = roles[selectedIndex || 0].role_id

    modules?.forEach((module: any) => {
      let indeterminate: boolean = false

      module?.permissions?.forEach((perm: any) => {
        if (perm?.is_assigned) {
          indeterminate = true
        }
      })

      currentModules.push({
        ...module,
        role: role,
        open: false,
        indeterminate: indeterminate,
      })
    })

    dispatch(setPerms([...currentModules]))

    dispatch(setLoading(false))
  }

  return (
    <>
      <NewRoleModal
        open={open}
        setOpen={setOpen}
        handleCreateNewRole={handleCreateNewRole}
        handleEditRole={handleEditRole}
        loading={loader}
        editState={editState}
        setEditState={setEditState}
        isDisabled={isDisabled}
        setIsDisabled={setIsDisabled}
      />
      <SimpleConfirmation
        open={roleDeleteConfirmationOpen}
        setOpen={setRoleDeleteConfirmationOpen}
        title={`Do you want to delete this Role?`}
        buttonText={`Delete`}
        confirmActionHandler={handleDeleteRole}
      />
      <Box
        sx={{
          height: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            height: "40px",
            display: "flex",
            padding: "0px 25px",
            alignItems: "center",
            justifyContent: "flex-start",
            backgroundColor: "#3366D6",
            color: "#FFFFFF",
          }}
        >
          <Typography fontSize={14}>Role List</Typography>
        </Box>
        <Tabs
          orientation="vertical"
          variant="scrollable"
          scrollButtons={false}
          sx={{
            flex: 1,
            flexDirection: "column",
            width: "100%",
            backgroundColor: "#F7F7F7",
          }}
        >
          {allPerms?.includes(permissions?.CREATE_ROLES) ? (
            <Tab
              onClick={() => setOpen(true)}
              sx={{ padding: 0 }}
              label={
                <Box
                  sx={{
                    height: "60px",
                    display: "flex",
                    width: "100%",
                    borderTop: "1px solid #E3E3E3",
                    borderBottom: "1px solid #E3E3E3",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    paddingLeft: "25px",
                    color: colors.primaryBlue,
                  }}
                >
                  + New
                </Box>
              }
            />
          ) : null}
          {roles?.map((role: RoleType, index: any) => (
            <Tab
              // onClick={() => handleRoleTypeSelection(index)}
              sx={{ padding: 0 }}
              label={
                <Box
                  sx={{
                    height: "60px",
                    display: "flex",
                    width: "100%",
                    borderTop: "1px solid #E3E3E3",
                    borderBottom: "1px solid #E3E3E3",
                  }}
                >
                  <Box
                    sx={{
                      width: "13px",
                      backgroundColor:
                        selectedRoleType == index ? "#3366D6" : "#F7F7F7",
                    }}
                  ></Box>
                  <Box
                    onClick={() => handleRoleTypeSelection(index)}
                    sx={{
                      flex: 1,
                      padding: "0px 12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor:
                        selectedRoleType == index ? "#FFFFFF" : "#F7F7F7",
                    }}
                  >
                    <Typography
                      sx={{
                        flex: 2,
                        display: "flex",
                        alignItems: "flex-start",
                        whiteSpace: "nowrap",
                      }}
                      fontWeight={500}
                      fontSize={14}
                      color={"#000000"}
                    >
                      {role?.role_display_name}
                    </Typography>
                    {/* <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                                            <Typography sx={{ flex: 1 }} fontWeight={500} fontSize={14} color={"#3C77EF"}>
                                                {role?.count}
                                            </Typography>
                                            <IconButton sx={{ flex: 1 }}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </Box> */}
                  </Box>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#FFFFFF",
                    }}
                    // onClick={handleDeleteRole}
                  >
                    <RoleEditMenu
                      values="state"
                      currentSeleted={role}
                      handleRoleEditOpen={setOpen}
                      handleRoleDeleteOpen={setRoleDeleteConfirmationOpen}
                      setEditState={setEditState}
                      setDeleteRoleId={setDeleteRoleId}
                    />
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>
    </>
  )
}

export default RolesTable
